// Parses navigator.userAgent into a human-readable browser/engine/OS/device identity, and
// collects the other navigator signals a site can read alongside it. Browser-only — call from
// a client component's effect. No hashing, no async probes: everything here is a plain,
// synchronous read except the optional Client Hints high-entropy values, which the browser
// gates behind a promise on purpose.

export type DeviceType = "desktop" | "mobile" | "tablet" | "bot";

export interface ParsedUA {
  browserName: string;
  browserVersion: string;
  engineName: string;
  engineVersion: string;
  osName: string;
  osVersion: string;
  deviceType: DeviceType;
  /** Set when the UA string identifies a known in-app browser (e.g. Instagram, Facebook). */
  inAppBrowser: string | null;
}

const IN_APP_BROWSERS: [RegExp, string][] = [
  [/FBAN|FBAV/, "Facebook"],
  [/Instagram/, "Instagram"],
  [/MicroMessenger/, "WeChat"],
  [/\bLine\//, "LINE"],
  [/TikTok/, "TikTok"],
  [/Twitter/, "Twitter/X"],
  [/Snapchat/, "Snapchat"],
];

const BOT_PATTERN =
  /bot|crawl|spider|slurp|facebookexternalhit|bingpreview|googlebot|bingbot|duckduckbot|baiduspider|yandexbot|semrushbot|ahrefsbot|mj12bot|petalbot|applebot|headlesschrome/i;

function detectInAppBrowser(ua: string): string | null {
  for (const [pattern, name] of IN_APP_BROWSERS) {
    if (pattern.test(ua)) return name;
  }
  return null;
}

interface BrowserMatch {
  name: string;
  version: string;
}

/** Order matters: every Blink and WebKit browser also carries a "Safari" token, so the more
 * specific brands must be checked first. */
function detectBrowser(ua: string): BrowserMatch {
  const checks: [RegExp, string][] = [
    [/Edg\/([\d.]+)/, "Edge"],
    [/SamsungBrowser\/([\d.]+)/, "Samsung Internet"],
    [/OPR\/([\d.]+)/, "Opera"],
    [/Opera\/([\d.]+)/, "Opera"],
    [/Firefox\/([\d.]+)/, "Firefox"],
    [/CriOS\/([\d.]+)/, "Chrome"],
    [/Chrome\/([\d.]+)/, "Chrome"],
    [/Version\/([\d.]+).*Safari/, "Safari"],
    [/MSIE ([\d.]+)/, "Internet Explorer"],
    [/Trident.*rv:([\d.]+)/, "Internet Explorer"],
  ];
  for (const [pattern, name] of checks) {
    const match = ua.match(pattern);
    if (match) return { name, version: match[1] };
  }
  return { name: "Unknown browser", version: "" };
}

interface EngineMatch {
  name: string;
  version: string;
}

function detectEngine(ua: string, browser: BrowserMatch): EngineMatch {
  if (browser.name === "Firefox") {
    const rv = ua.match(/rv:([\d.]+)/);
    return { name: "Gecko", version: rv?.[1] ?? "" };
  }
  if (/Trident/.test(ua)) {
    const version = ua.match(/Trident\/([\d.]+)/)?.[1] ?? "";
    return { name: "Trident", version };
  }
  const isBlinkBrowser = ["Chrome", "Edge", "Opera", "Samsung Internet"].includes(browser.name);
  if (isBlinkBrowser) return { name: "Blink", version: browser.version };
  if (/AppleWebKit/.test(ua)) {
    const version = ua.match(/AppleWebKit\/([\d.]+)/)?.[1] ?? "";
    return { name: "WebKit", version };
  }
  return { name: "Unknown engine", version: "" };
}

const WINDOWS_VERSIONS: Record<string, string> = {
  "10.0": "10 or 11",
  "6.3": "8.1",
  "6.2": "8",
  "6.1": "7",
  "6.0": "Vista",
  "5.2": "XP",
  "5.1": "XP",
};

interface OsMatch {
  name: string;
  version: string;
}

/** `touchPoints` disambiguates iPadOS 13+, which reports as a plain "Macintosh" desktop Safari
 * UA and is only distinguishable by its multi-touch screen. */
function detectOs(ua: string, touchPoints: number): OsMatch {
  const windows = ua.match(/Windows NT ([\d.]+)/);
  if (windows) return { name: "Windows", version: WINDOWS_VERSIONS[windows[1]] ?? windows[1] };

  if (/CrOS/.test(ua)) {
    const version = ua.match(/CrOS [\w]+ ([\d.]+)/)?.[1] ?? "";
    return { name: "Chrome OS", version };
  }

  const android = ua.match(/Android ([\d.]+)/);
  if (android) return { name: "Android", version: android[1] };

  const iosMatch = ua.match(/(?:iPhone OS|CPU OS) ([\d_]+)/);
  if (iosMatch)
    return { name: /iPad/.test(ua) ? "iPadOS" : "iOS", version: iosMatch[1].replace(/_/g, ".") };

  const macMatch = ua.match(/Mac OS X ([\d_]+)/);
  if (macMatch) {
    const version = macMatch[1].replace(/_/g, ".");
    // A touch-capable "Macintosh" UA is iPadOS requesting the desktop site, its default since
    // iPadOS 13 — the UA string alone is otherwise identical to real macOS Safari.
    if (/Macintosh/.test(ua) && touchPoints > 1) return { name: "iPadOS", version: "" };
    return { name: "macOS", version };
  }

  if (/Linux/.test(ua)) return { name: "Linux", version: "" };

  return { name: "Unknown OS", version: "" };
}

function detectDeviceType(ua: string, osName: string): DeviceType {
  if (BOT_PATTERN.test(ua)) return "bot";
  if (osName === "iPadOS") return "tablet";
  if (/iPhone/.test(ua)) return "mobile";
  if (/Android/.test(ua)) return /Mobile/.test(ua) ? "mobile" : "tablet";
  if (/Tablet|PlayBook|Kindle/.test(ua)) return "tablet";
  if (/Mobi/.test(ua)) return "mobile";
  return "desktop";
}

export const DEVICE_TYPE_LABEL: Record<DeviceType, string> = {
  desktop: "Desktop",
  mobile: "Mobile",
  tablet: "Tablet",
  bot: "Bot / crawler",
};

export function parseUserAgent(ua: string, touchPoints = 0): ParsedUA {
  const browser = detectBrowser(ua);
  const engine = detectEngine(ua, browser);
  const os = detectOs(ua, touchPoints);
  return {
    browserName: browser.name,
    browserVersion: browser.version,
    engineName: engine.name,
    engineVersion: engine.version,
    osName: os.name,
    osVersion: os.version,
    deviceType: detectDeviceType(ua, os.name),
    inAppBrowser: detectInAppBrowser(ua),
  };
}

// --- User-Agent Client Hints (navigator.userAgentData) --------------------------------------
// Chromium's structured replacement for the User-Agent string. Not yet in TypeScript's DOM lib
// and not implemented by Firefox/Safari, hence the local types and the optional access.

export interface UABrand {
  brand: string;
  version: string;
}

interface UADataValues {
  architecture?: string;
  bitness?: string;
  platformVersion?: string;
  uaFullVersion?: string;
  fullVersionList?: UABrand[];
  model?: string;
}

interface NavigatorUAData {
  brands: UABrand[];
  mobile: boolean;
  platform: string;
  getHighEntropyValues(hints: string[]): Promise<UADataValues>;
}

type NavigatorWithUAData = Navigator & { userAgentData?: NavigatorUAData };

interface NetworkInformation {
  effectiveType?: string;
  downlink?: number;
}

type NavigatorWithExtras = Navigator & {
  deviceMemory?: number;
  connection?: NetworkInformation;
  pdfViewerEnabled?: boolean;
};

const HIGH_ENTROPY_HINTS = [
  "architecture",
  "bitness",
  "platformVersion",
  "uaFullVersion",
  "fullVersionList",
  "model",
];

function formatBrands(brands?: UABrand[]): string {
  if (!brands?.length) return "";
  return brands.map((b) => `${b.brand} ${b.version}`).join(" · ");
}

/** First non-GREASE brand ("Not)A;Brand" etc. is deliberate noise, not a real browser). */
function realBrand(brands?: UABrand[]): UABrand | undefined {
  return brands?.find((b) => !/not.a.brand/i.test(b.brand));
}

export interface UaSignal {
  category: string;
  label: string;
  value: string;
}

export interface UaReport {
  raw: string;
  parsed: ParsedUA;
  signals: UaSignal[];
  /** Low-entropy Client Hints brands, kept alongside the formatted "Brands" signal so callers can
   * check for specific brands programmatically (e.g. detecting Brave). Empty when Client Hints
   * isn't supported. */
  brands: UABrand[];
}

/** Everything readable synchronously, in one pass. Call once on mount. */
export function collectUaSignals(): UaReport {
  const ua = navigator.userAgent;
  const parsed = parseUserAgent(ua, navigator.maxTouchPoints);
  const nav = navigator as NavigatorWithUAData & NavigatorWithExtras;
  const signals: UaSignal[] = [];
  const add = (category: string, label: string, value: string) =>
    signals.push({ category, label, value });

  // Browser & platform
  // Labelled "(User-Agent string)" for the same reason as the OS row below: some browsers
  // (Brave, chiefly) deliberately mirror another browser's UA string, so this row can disagree
  // with the headline above, which prefers Client Hints when it can identify the real browser.
  add(
    "Browser & platform",
    "Browser (User-Agent string)",
    `${parsed.browserName} ${parsed.browserVersion}`.trim()
  );
  add(
    "Browser & platform",
    "Rendering engine",
    `${parsed.engineName}${parsed.engineVersion ? ` ${parsed.engineVersion}` : ""}`
  );
  // Labelled "(User-Agent string)" because it can be less accurate than the Client Hints platform
  // rows below on the same page — Chromium reduces this exact value on macOS and Windows. The
  // headline above prefers Client Hints when it can; this row always shows what the string itself
  // says, so the two can be compared side by side instead of one silently overriding the other.
  add(
    "Browser & platform",
    "Operating system (User-Agent string)",
    `${parsed.osName}${parsed.osVersion ? ` ${parsed.osVersion}` : ""}`
  );
  add("Browser & platform", "Device type", DEVICE_TYPE_LABEL[parsed.deviceType]);
  if (parsed.inAppBrowser) add("Browser & platform", "In-app browser", parsed.inAppBrowser);
  add("Browser & platform", "User agent", ua);
  // Labelled "Navigator platform" to distinguish it from the Client Hints platform row below —
  // both are correct but come from different APIs, and can disagree (e.g. Apple Silicon).
  add("Browser & platform", "Navigator platform", nav.platform || "unavailable");
  add("Browser & platform", "Vendor", navigator.vendor || "unavailable");
  add("Browser & platform", "Cookies", navigator.cookieEnabled ? "enabled" : "disabled");
  const dnt = navigator.doNotTrack;
  add(
    "Browser & platform",
    "Do Not Track",
    dnt === "1" ? "enabled" : dnt === "0" ? "disabled" : "unset"
  );
  if (typeof nav.pdfViewerEnabled === "boolean") {
    add("Browser & platform", "PDF viewer", nav.pdfViewerEnabled ? "built-in" : "none");
  }

  // Client Hints — low-entropy values only; the high-entropy ones require a separate async
  // request and are merged in later by collectClientHintDetails.
  const uaData = nav.userAgentData;
  if (!uaData) {
    add("Client Hints", "Client Hints", "not supported in this browser");
  } else {
    add("Client Hints", "Brands", formatBrands(uaData.brands) || "unavailable");
    add("Client Hints", "Mobile", uaData.mobile ? "yes" : "no");
    add("Client Hints", "Client Hints platform", uaData.platform || "unavailable");
  }

  // Language & time
  add("Language & time", "Language", navigator.language || "unavailable");
  add(
    "Language & time",
    "Languages",
    navigator.languages?.join(", ") || navigator.language || "unavailable"
  );
  add("Language & time", "Timezone", Intl.DateTimeFormat().resolvedOptions().timeZone);
  add(
    "Language & time",
    "Timezone offset",
    `UTC${formatUtcOffset(new Date().getTimezoneOffset())}`
  );

  // Screen & display
  add(
    "Screen & display",
    "Screen resolution",
    `${screen.width}×${screen.height} @${window.devicePixelRatio}x · ${screen.colorDepth}-bit`
  );
  add("Screen & display", "Available area", `${screen.availWidth}×${screen.availHeight}`);
  add("Screen & display", "Orientation", screen.orientation?.type ?? "unavailable");
  add(
    "Screen & display",
    "Color scheme",
    window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light"
  );
  add(
    "Screen & display",
    "Reduced motion",
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "reduce" : "no-preference"
  );

  // Hardware & network
  add(
    "Hardware & network",
    "CPU threads",
    navigator.hardwareConcurrency ? String(navigator.hardwareConcurrency) : "unavailable"
  );
  add(
    "Hardware & network",
    "Device memory",
    nav.deviceMemory ? `${nav.deviceMemory} GB` : "unavailable"
  );
  add("Hardware & network", "Touch points", String(navigator.maxTouchPoints ?? 0));
  add(
    "Hardware & network",
    "Pointer type",
    window.matchMedia("(pointer: coarse)").matches
      ? "coarse (touch)"
      : window.matchMedia("(pointer: fine)").matches
        ? "fine (mouse / trackpad)"
        : "unavailable"
  );
  // "Effective connection", not "Network": effectiveType is the browser's own estimate of
  // connection QUALITY (bucketed by measured round-trip time and downlink), not the physical
  // access technology. It reads "4g" on a fast wired or Wi-Fi connection just as often as on an
  // actual cellular link — labelling it "Network: 4g" implies a radio technology this API never
  // measures and doesn't have.
  const conn = nav.connection;
  add(
    "Hardware & network",
    "Effective connection (browser estimate)",
    conn?.effectiveType
      ? `${conn.effectiveType}${conn.downlink ? ` · ${conn.downlink} Mbps` : ""}`
      : "unavailable"
  );

  return { raw: ua, parsed, signals, brands: uaData?.brands ?? [] };
}

function formatUtcOffset(minutesBehindUtc: number): string {
  const total = -minutesBehindUtc;
  const sign = total >= 0 ? "+" : "-";
  const abs = Math.abs(total);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return `${sign}${hours}${minutes ? `:${String(minutes).padStart(2, "0")}` : ""}`;
}

/** The high-entropy Client Hints values. Chromium gates these behind a promise on purpose
 * (they're more identifying than the rest of the UA string), so they arrive a tick after the
 * synchronous report and are merged in separately. Resolves to an empty list on any browser
 * without Client Hints, or if the request fails. */
export async function collectClientHintDetails(): Promise<UaSignal[]> {
  const uaData = (navigator as NavigatorWithUAData).userAgentData;
  if (!uaData) return [];
  try {
    const high = await uaData.getHighEntropyValues(HIGH_ENTROPY_HINTS);
    const out: UaSignal[] = [];
    const category = "Client Hints";
    if (high.platformVersion)
      out.push({ category, label: "Platform version", value: high.platformVersion });
    if (high.architecture) out.push({ category, label: "Architecture", value: high.architecture });
    if (high.bitness) out.push({ category, label: "Bitness", value: high.bitness });
    const fullVersion = high.uaFullVersion || realBrand(high.fullVersionList)?.version;
    if (fullVersion) out.push({ category, label: "Full browser version", value: fullVersion });
    if (high.model) out.push({ category, label: "Model", value: high.model });
    return out;
  } catch {
    return [];
  }
}

// --- Preferring Client Hints over the (possibly reduced) UA string for the OS identity --------

export interface ResolvedOs {
  name: string;
  version: string;
  /** "client-hints" when Client Hints resolved something the raw UA string can't — a real macOS
   * version instead of Chromium's frozen 10.15.7, or Windows 10 vs 11. "user-agent" everywhere
   * else (Firefox, Safari, Linux, Chrome OS, or any browser without Client Hints). */
  source: "client-hints" | "user-agent";
}

/**
 * Chromium's Sec-CH-UA-Platform-Version on Windows encodes the release as a
 * Windows.Foundation.UniversalApiContract version, not a marketing OS version: "1.0.0" through
 * "10.0.0" cover every Windows 10 feature update in turn, and "13.0.0" and up is Windows 11 (11
 * and 12 are unused). The raw UA string can't make this distinction at all — Windows 11 still
 * reports "Windows NT 10.0" for compatibility.
 */
function windowsVersionFromPlatformVersion(platformVersion: string): string | null {
  const major = Number.parseInt(platformVersion.split(".")[0] ?? "", 10);
  if (Number.isNaN(major)) return null;
  return major >= 13 ? "11" : "10";
}

/**
 * The more trustworthy OS identity when Client Hints can supply one. The raw User-Agent string is
 * deliberately reduced on both platforms this covers: Chromium freezes the reported macOS version
 * to 10.15.7 for every Mac, and Windows 11 reports itself identically to Windows 10. Client Hints
 * isn't frozen the same way, so it wins here — falling back to the UA-string parse on any browser
 * (Firefox, Safari) or platform (Linux, Chrome OS, Android, iOS) Client Hints doesn't help with.
 */
export function resolveDisplayOs(
  parsed: ParsedUA,
  clientHintPlatform: string | undefined,
  clientHintPlatformVersion: string | undefined
): ResolvedOs {
  if (clientHintPlatform === "macOS" && clientHintPlatformVersion) {
    return { name: "macOS", version: clientHintPlatformVersion, source: "client-hints" };
  }
  if (clientHintPlatform === "Windows" && clientHintPlatformVersion) {
    const version = windowsVersionFromPlatformVersion(clientHintPlatformVersion);
    if (version) return { name: "Windows", version, source: "client-hints" };
  }
  return { name: parsed.osName, version: parsed.osVersion, source: "user-agent" };
}

/** Explains the gap between the two sources in the specific terms of what's actually unreliable
 * about the raw UA string on this OS — only when Client Hints resolved something more accurate. */
export function osAccuracyNote(parsed: ParsedUA, resolved: ResolvedOs): string | null {
  if (resolved.source !== "client-hints") return null;
  if (parsed.osName === "macOS" && parsed.osVersion !== resolved.version) {
    return `Chromium browsers report macOS ${parsed.osVersion} in the user agent string for every Mac — a fixed value since Chrome's User-Agent Reduction. Client Hints reports the real version: macOS ${resolved.version}.`;
  }
  if (parsed.osName === "Windows" && parsed.osVersion === "10 or 11") {
    return `The user agent string can't tell Windows 10 and 11 apart — Windows 11 still reports itself as "Windows NT 10.0". Client Hints resolves it: Windows ${resolved.version}.`;
  }
  return null;
}

// --- Detecting Brave behind its Chrome-mirrored user agent string -----------------------------
// Brave deliberately mirrors Chromium's UA string byte-for-byte for site compatibility, so
// parseUserAgent alone always reports "Chrome". Two independent signals see through the mask:
// Client Hints' low-entropy `brands` list (a real, non-GREASE "Brave" brand Brave adds itself),
// and navigator.brave.isBrave(), a method Brave ships specifically for this. Either is
// authoritative; the raw UA string never is.

interface NavigatorWithBrave {
  brave?: { isBrave: () => Promise<boolean> };
}

function detectBraveBrand(brands: UABrand[]): boolean {
  return brands.some((b) => /^brave$/i.test(b.brand));
}

/** Resolves to false (never throws) on every non-Brave browser, and on the rare case the method
 * itself misbehaves. */
export async function checkIsBrave(): Promise<boolean> {
  try {
    const brave = (navigator as NavigatorWithBrave).brave;
    return brave ? await brave.isBrave() : false;
  } catch {
    return false;
  }
}

export interface ResolvedBrowser {
  name: string;
  version: string;
  source: "client-hints" | "user-agent";
}

export function resolveDisplayBrowser(
  parsed: ParsedUA,
  clientHintBrands: UABrand[],
  isBraveConfirmed: boolean
): ResolvedBrowser {
  if (parsed.browserName === "Chrome" && (isBraveConfirmed || detectBraveBrand(clientHintBrands))) {
    return { name: "Brave", version: parsed.browserVersion, source: "client-hints" };
  }
  return { name: parsed.browserName, version: parsed.browserVersion, source: "user-agent" };
}

/** Mirrors osAccuracyNote's shape: explains the one case a resolved value disagrees with the raw
 * UA string parse, in terms specific to why the string can't be trusted here. */
export function browserAccuracyNote(parsed: ParsedUA, resolved: ResolvedBrowser): string | null {
  if (resolved.source !== "client-hints" || resolved.name !== "Brave") return null;
  return `Brave mirrors Chrome's user agent string for compatibility, so it always reads "${parsed.browserName} ${parsed.browserVersion}" here. Client Hints and navigator.brave.isBrave() confirm the real browser: Brave.`;
}
